// src/pages/dashboard/components/MyCommissionCard.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, ChevronRight, CheckCircle, PlusCircle, Award } from 'lucide-react';
import { getMyCommissionSummary } from '../../../services/api/employeeCommission';
import styles from './MyCommissionCard.module.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatLKR = (amount) => {
  const num = parseFloat(amount) || 0;
  return num.toLocaleString('en-LK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatRate = (rate, basis) => {
  const val = parseFloat(rate).toFixed(2);
  return basis === 'order_value' ? `${val}%` : `LKR ${val}`;
};

const basisLabel = (basis) => {
  const map = {
    order_count: 'per order',
    order_quantity: 'per item',
    order_value: '% of value',
  };
  return map[basis] || basis;
};

const getMonthName = () => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return months[new Date().getMonth()];
};

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const Skeleton = () => (
  <div className={styles.container}>
    <div className={styles.header}>
      <div className={styles.skeletonTitle} />
      <div className={styles.skeletonBadge} />
    </div>
    <div className={styles.skeletonTotal} />
    <div className={styles.skeletonGrid}>
      <div className={styles.skeletonPanel} />
      <div className={styles.skeletonPanel} />
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const MyCommissionCard = ({ gridItemClassName }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      const res = await getMyCommissionSummary();
      setData(res);
    } catch (err) {
      console.error('MyCommissionCard error:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  // Don't render anything if loading failed or user is not eligible
  if (loading) return (
    <div className={gridItemClassName}>
      <Skeleton />
    </div>
  );
  if (error || !data || !data.eligible) return null;

  const {
    confirmed,
    created,
    totalCommission,
    confirmCommissionRate,
    createCommissionRate,
    calculationBasis,
  } = data;

  const hasActivity = confirmed.orderCount > 0 || created.orderCount > 0;
  const monthName = getMonthName();

  return (
    <div className={gridItemClassName}>
        <div className={styles.container}>
        {/* ── Header ── */}
        <div className={styles.header}>
            <div className={styles.headerLeft}>
            <div className={styles.iconWrap}>
                <Award size={18} />
            </div>
            <div>
                <h3 className={styles.title}>My Commission</h3>
                <p className={styles.subtitle}>{monthName} {new Date().getFullYear()}</p>
            </div>
            </div>
            <Link to="/hr/commission/my-report" className={styles.viewLink}>
            View History
            <ChevronRight size={14} />
            </Link>
        </div>

        {/* ── Total Earned Hero ── */}
        <div className={styles.totalSection}>
            <div className={styles.totalLabel}>Total Earned This Month</div>
            <div className={styles.totalValue}>
            <span className={styles.totalCurrency}>LKR</span>
            <span className={styles.totalAmount}>{formatLKR(totalCommission)}</span>
            </div>
            {!hasActivity && (
            <p className={styles.noActivityNote}>
                No delivered &amp; invoiced orders yet this month
            </p>
            )}
        </div>

        {/* ── Confirmed / Created Panels ── */}
        <div className={styles.panelsRow}>

            {/* Confirmed Orders */}
            <div className={styles.panel}>
            <div className={styles.panelHeader}>
                <div className={`${styles.panelIcon} ${styles.panelIconConfirm}`}>
                <CheckCircle size={14} />
                </div>
                <span className={styles.panelTitle}>Confirmed</span>
                <span className={`${styles.rateBadge} ${styles.rateBadgeConfirm}`}>
                {formatRate(confirmCommissionRate, calculationBasis)}
                <span className={styles.rateBasis}>&nbsp;/ {basisLabel(calculationBasis)}</span>
                </span>
            </div>

            <div className={styles.statsGrid}>
                <div className={styles.stat}>
                <span className={styles.statLabel}>Orders</span>
                <span className={styles.statValue}>{confirmed.orderCount}</span>
                </div>
                <div className={styles.stat}>
                <span className={styles.statLabel}>Items</span>
                <span className={styles.statValue}>{confirmed.orderQty}</span>
                </div>
                {/* <div className={styles.stat}>
                <span className={styles.statLabel}>Order Value</span>
                <span className={styles.statValue}>LKR {formatLKR(confirmed.orderValue)}</span>
                </div> */}
            </div>

            <div className={styles.panelCommission}>
                <span className={styles.panelCommissionLabel}>Commission</span>
                <span className={`${styles.panelCommissionValue} ${styles.panelCommissionValueConfirm}`}>
                LKR {formatLKR(confirmed.commissionEarned)}
                </span>
            </div>
            </div>

            {/* Created Orders */}
            <div className={styles.panel}>
            <div className={styles.panelHeader}>
                <div className={`${styles.panelIcon} ${styles.panelIconCreate}`}>
                <PlusCircle size={14} />
                </div>
                <span className={styles.panelTitle}>Created</span>
                <span className={`${styles.rateBadge} ${styles.rateBadgeCreate}`}>
                {formatRate(createCommissionRate, calculationBasis)}
                <span className={styles.rateBasis}>&nbsp;/ {basisLabel(calculationBasis)}</span>
                </span>
            </div>

            <div className={styles.statsGrid}>
                <div className={styles.stat}>
                <span className={styles.statLabel}>Orders</span>
                <span className={styles.statValue}>{created.orderCount}</span>
                </div>
                <div className={styles.stat}>
                <span className={styles.statLabel}>Items</span>
                <span className={styles.statValue}>{created.orderQty}</span>
                </div>
                {/* <div className={styles.stat}>
                <span className={styles.statLabel}>Order Value</span>
                <span className={styles.statValue}>LKR {formatLKR(created.orderValue)}</span>
                </div> */}
            </div>

            <div className={styles.panelCommission}>
                <span className={styles.panelCommissionLabel}>Commission</span>
                <span className={`${styles.panelCommissionValue} ${styles.panelCommissionValueCreate}`}>
                LKR {formatLKR(created.commissionEarned)}
                </span>
            </div>
            </div>
        </div>

        {/* ── Footer ── */}
        <div className={styles.footer}>
            <TrendingUp size={12} />
            <span>
            Based on delivered &amp; invoiced orders &middot; {basisLabel(calculationBasis)} basis
            </span>
        </div>
        </div>
    </div>
  );
};

export default MyCommissionCard;